import { useEffect } from 'react';
import { SITE_URL } from '../components/landing/landingFacts';

type PageMeta = {
    title: string;
    description?: string;
    /** Path of the canonical URL, e.g. "/privacy". Omit for pages that should not be indexed. */
    path?: string;
    noIndex?: boolean;
    /** Absolute URL of the image used by link previews (og:image). */
    image?: string;
    /** Structured data for search engines (schema.org), written as JSON-LD. */
    jsonLd?: object;
};

function setMeta(selector: string, attribute: 'content' | 'href', value: string | null) {
    let element = document.head.querySelector<HTMLElement>(selector);
    if (value === null) { element?.remove(); return; }
    if (!element) {
        const match = selector.match(/^(meta|link)\[(name|property|rel)="([^"]+)"\]$/);
        if (!match) return;
        element = document.createElement(match[1]);
        element.setAttribute(match[2], match[3]);
        document.head.appendChild(element);
    }
    element.setAttribute(attribute, value);
}

/** Title, description, canonical, robots, preview image and structured data for a public page. */
export function usePageMeta({ title, description, path, noIndex, image, jsonLd }: PageMeta) {
    const structured = jsonLd ? JSON.stringify(jsonLd) : null;
    useEffect(() => {
        const previousTitle = document.title;
        document.title = title;
        if (description) {
            setMeta('meta[name="description"]', 'content', description);
            setMeta('meta[property="og:description"]', 'content', description);
        }
        setMeta('meta[property="og:title"]', 'content', title);
        const url = path ? `${SITE_URL}${path}` : null;
        setMeta('link[rel="canonical"]', 'href', url);
        if (url) setMeta('meta[property="og:url"]', 'content', url);
        setMeta('meta[name="robots"]', 'content', noIndex ? 'noindex' : null);
        const previousImage = document.head.querySelector('meta[property="og:image"]')?.getAttribute('content') ?? null;
        if (image) setMeta('meta[property="og:image"]', 'content', image);

        let script: HTMLScriptElement | null = null;
        if (structured) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.textContent = structured;
            document.head.appendChild(script);
        }
        return () => {
            document.title = previousTitle;
            if (image) setMeta('meta[property="og:image"]', 'content', previousImage);
            script?.remove();
        };
    }, [title, description, path, noIndex, image, structured]);
}
